/** Cave validation (docs/spec/03 §5): errors stop a cave shipping; so do warnings, in the sets. */

import { E, isButterfly } from '../sim/elements';
import { buildCave, mapSize } from './build';
import { DEEP_CHARS, LEGEND, MAP_CHARS } from './legend';
import type { BuiltCave, CaveDef } from './types';

export interface CaveIssue {
  /** 1-5, or 0 for the cave as a whole. */
  readonly level: number;
  readonly severity: 'error' | 'warning';
  readonly message: string;
}

export type CaveKind = 'classic' | 'deep' | 'custom';

const NAME_RE = /^[A-Z0-9 .!'-]{1,16}$/;
const BLOCKING = new Set<number>([E.STEEL, E.BRICK, E.MAGIC_WALL, E.EXPANDING_WALL, E.SLIME]);

/** Cells Digby might reach from the inbox if every boulder could be dug around (an optimistic flood). */
export function reachable(b: Pick<BuiltCave, 'w' | 'h' | 'cells'>): Uint8Array {
  const seen = new Uint8Array(b.w * b.h);
  const start = b.cells.indexOf(E.INBOX);
  if (start < 0) return seen;
  const queue = [start];
  seen[start] = 1;
  while (queue.length > 0) {
    const i = queue.pop() ?? 0;
    const x = i % b.w;
    const y = Math.floor(i / b.w);
    const next = [x > 0 ? i - 1 : -1, x < b.w - 1 ? i + 1 : -1, y > 0 ? i - b.w : -1, y < b.h - 1 ? i + b.w : -1];
    for (const n of next) {
      if (n < 0 || seen[n] === 1) continue;
      const el = b.cells[n] ?? E.STEEL;
      if (BLOCKING.has(el) || el === E.OUTBOX_CLOSED || el === E.INBOX) continue;
      seen[n] = 1;
      queue.push(n);
    }
  }
  return seen;
}

/** Diamonds that could exist and be reached: on the map, from butterflies, amoeba and magic walls. */
export function diamondPotential(b: BuiltCave): { onMap: number; reachableOnMap: number; potential: number } {
  const seen = reachable(b);
  let onMap = 0;
  let reachableOnMap = 0;
  let butterflies = 0;
  let amoeba = 0;
  let boulders = 0;
  let magic = 0;
  b.cells.forEach((el, i) => {
    if (el === E.DIAMOND) {
      onMap++;
      if (seen[i] === 1) reachableOnMap++;
    } else if (isButterfly(el)) butterflies++;
    else if (el === E.AMOEBA) amoeba++;
    else if (el === E.BOULDER) boulders++;
    else if (el === E.MAGIC_WALL) magic++;
  });
  const fromAmoeba = amoeba > 0 ? 40 : 0;
  const fromMagic = magic > 0 ? Math.min(boulders, magic * 6) : 0;
  return { onMap, reachableOnMap, potential: reachableOnMap + butterflies * 9 + fromAmoeba + fromMagic };
}

function checkMap(def: CaveDef, kind: CaveKind, out: CaveIssue[]): void {
  const err = (message: string): void => void out.push({ level: 0, severity: 'error', message });
  const { w, h } = mapSize(def);
  if (def.map.some((row) => row.length !== w)) err('rows are not all the same length');
  const bad = new Set<string>();
  for (const row of def.map) for (const ch of row) if (!MAP_CHARS.has(ch)) bad.add(ch);
  if (bad.size > 0) err(`unknown map characters: ${[...bad].map((c) => JSON.stringify(c)).join(' ')}`);
  const count = (ch: string): number => def.map.reduce((n, row) => n + row.split(ch).length - 1, 0);
  if (count('P') !== 1) err(`needs exactly one inbox P, has ${count('P')}`);
  if (count('X') < 1) err('needs an exit X');
  if (kind === 'custom') {
    if (w < 12 || h < 8 || w > 80 || h > 50) err(`size ${w}x${h} is outside 12x8 to 80x50`);
  } else if (def.intermission ? w !== 20 || h !== 12 : w !== 40 || h !== 22) {
    err(`size ${w}x${h}; ${def.intermission ? 'intermissions are 20x12' : 'caves are 40x22'}`);
  }
  const fillChars = [def.fill?.base ?? '.', ...(def.fill?.items ?? []).map(([c]) => c)];
  for (const c of fillChars) if (LEGEND[c] === undefined) err(`fill uses unknown character ${JSON.stringify(c)}`);
  const total = (def.fill?.items ?? []).reduce((n, [, p]) => n + p, 0);
  if (total > 100) err(`fill items add up to ${total} %`);
  if (kind === 'classic') {
    const deep = [...def.map.join(''), ...fillChars].filter((c) => DEEP_CHARS.has(c));
    if (deep.length > 0) err(`Deep elements in a Classic cave: ${[...new Set(deep)].join(' ')}`);
  }
  const uses = (c: string): boolean => def.map.some((row) => row.includes(c)) || fillChars.includes(c);
  if (uses('m') && (def.magicWallTime ?? 0) <= 0) err('has a magic wall but no magicWallTime');
  if (uses('a') && def.amoebaTime === undefined) out.push({ level: 0, severity: 'warning', message: 'has amoeba but no amoebaTime' });
}

function checkMeta(def: CaveDef, kind: CaveKind, out: CaveIssue[]): void {
  const err = (message: string): void => void out.push({ level: 0, severity: 'error', message });
  if (!NAME_RE.test(def.name)) err(`name ${JSON.stringify(def.name)}: 1-16 of A-Z 0-9 space . ! ' -`);
  if (def.hint.length > 60) err('hint is over 60 characters');
  if (kind !== 'custom' && def.levels.length !== 5) err(`needs 5 levels, has ${def.levels.length}`);
  if (def.levels.length < 1) err('needs at least one level');
  const [c1, c2, c3] = def.colors;
  if (def.colors.some((c) => !Number.isInteger(c) || c < 0 || c > 15)) err('colours must be 0-15');
  if (c1 === c2 || c2 === c3 || c1 === c3) err('the three colours must differ');
  if (def.colors.includes(0)) err('no cave colour may be black');
  if (def.diamondValue < 0 || def.extraValue < 0) err('diamond values must not be negative');
  def.levels.forEach((lv, i) => {
    if (lv.diamonds < 1) out.push({ level: i + 1, severity: 'error', message: 'quota below 1' });
    if (lv.time < 1 || lv.time > 999) out.push({ level: i + 1, severity: 'error', message: 'time outside 1-999' });
    if (lv.tick !== undefined && (lv.tick < 60 || lv.tick > 400)) {
      out.push({ level: i + 1, severity: 'error', message: 'tick outside 60-400 ms' });
    }
  });
}

function checkLevel(def: CaveDef, level: number, out: CaveIssue[]): void {
  const warn = (message: string): void => void out.push({ level, severity: 'warning', message });
  const b = buildCave(def, level);
  for (let x = 0; x < b.w; x++) {
    if (b.cells[x] !== E.STEEL || b.cells[(b.h - 1) * b.w + x] !== E.STEEL) return void warn('border is not all steel');
  }
  for (let y = 0; y < b.h; y++) {
    if (b.cells[y * b.w] !== E.STEEL || b.cells[y * b.w + b.w - 1] !== E.STEEL) return void warn('border is not all steel');
  }
  const { potential } = diamondPotential(b);
  if (b.params.diamondsNeeded > potential) warn(`quota ${b.params.diamondsNeeded} is more than the ${potential} diamonds that can be had`);
  const seen = reachable(b);
  const exits = [...b.cells.keys()].filter((i) => b.cells[i] === E.OUTBOX_CLOSED);
  const touches = (i: number): boolean => [i - 1, i + 1, i - b.w, i + b.w].some((n) => n >= 0 && n < seen.length && seen[n] === 1);
  if (exits.length > 0 && !exits.some(touches)) warn('the exit cannot be reached from the inbox');
  const inbox = b.cells.indexOf(E.INBOX);
  const above = b.cells[inbox - b.w];
  if (above === E.BOULDER || above === E.DIAMOND) warn('a boulder or diamond sits on the inbox');
}

export function validateCave(def: CaveDef, kind: CaveKind = 'custom'): CaveIssue[] {
  const out: CaveIssue[] = [];
  checkMeta(def, kind, out);
  checkMap(def, kind, out);
  if (out.some((i) => i.severity === 'error')) return out;
  for (let level = 1; level <= Math.max(1, Math.min(5, def.levels.length)); level++) checkLevel(def, level, out);
  return out;
}

/** Issues as lines for a test failure or the editor. */
export function describeIssues(def: CaveDef, issues: readonly CaveIssue[]): string[] {
  return issues.map((i) => `${def.id} ${def.name}${i.level > 0 ? ` L${i.level}` : ''}: ${i.severity}: ${i.message}`);
}
